/**
 * A step's arguments on its card, each with a port to wire into and a slot saying where its value
 * comes from. A slot opens the picker; a fixed value is typed in place. Required arguments always
 * show, optional ones once set or asked for.
 */

import { Plus, Sparkles, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ToolField } from '@/modules/back-testing/datasets/tools/tool-schema';
import type { CustomToolArg, CustomToolStep } from '@/types/custom-tool.types';
import { useCustomToolBuilder } from '../../builder/builder-context';
import { withArg } from '../../custom-tool-draft';
import { FixedValue } from '../../steps/fixed-value';
import { useShownFields } from '../../steps/step-fields';
import { argPort } from '../ports';
import { ArgPicker } from './arg-picker';
import { INTERACTIVE, Row } from './card';
import { Port, type PortTone } from './port';
import { usePortsChanged } from './use-ports-changed';

const CHIP = 'flex h-5 min-w-0 max-w-40 items-center gap-1 rounded-md border px-1.5 text-[10.5px] leading-4';

const argTone = (arg: CustomToolArg | undefined): PortTone => {
	if (!arg) return 'free';
	return arg.source === 'input' ? 'ai' : arg.source === 'step' ? 'step' : 'value';
};

const ClearButton = ({ field, onClear }: { field: string; onClear: () => void }) => (
	<button
		type='button'
		aria-label={`Clear ${field}`}
		onClick={onClear}
		className={cn(INTERACTIVE, 'shrink-0 rounded p-0.5 text-gray-700 opacity-0 group-hover/arg:opacity-100 hover:bg-gray-100 hover:text-ink focus-visible:opacity-100')}
	>
		<X className='size-3' />
	</button>
);

/** What the slot says: nothing yet, the AI input, or the step field it takes. */
const SlotContent = ({ arg, steps }: { arg: CustomToolArg | undefined; steps: CustomToolStep[] }) => {
	if (arg?.source === 'input') {
		return (
			<>
				<Sparkles className='size-3 shrink-0' aria-hidden />
				<span className='truncate font-mono'>{arg.input}</span>
			</>
		);
	}
	if (arg?.source === 'step') {
		const number = steps.findIndex((step) => step.id === arg.step) + 1;
		return <span className='truncate font-mono'>{`${number} · ${arg.path.slice(arg.path.lastIndexOf('.') + 1)}`}</span>;
	}
	return (
		<>
			<Plus className='size-3 shrink-0' aria-hidden />
			Set
		</>
	);
};

const slotTone = (arg: CustomToolArg | undefined, required: boolean): string => {
	if (arg?.source === 'input') return 'border-sky-200 bg-sky-50 text-sky-800';
	if (arg?.source === 'step') return 'border-violet-200 bg-violet-50 text-violet-800';
	return required ? 'border-dashed border-red-300 bg-red-50 text-red-700' : 'border-dashed border-gray-500 text-gray-800';
};

const ArgSlot = ({ step, field }: { step: CustomToolStep; field: ToolField }) => {
	const { draft, change } = useCustomToolBuilder();
	const arg = step.args[field.name];
	const set = (next: CustomToolArg | null) => change((current) => withArg(current, step.id, field.name, next));

	if (arg?.source === 'value') {
		return (
			<span className='group/arg flex min-w-0 items-center gap-0.5'>
				<FixedValue
					className={INTERACTIVE}
					field={field}
					value={arg.value}
					focusOnMount={arg.value === ''}
					onChange={(value) => change((current) => withArg(current, step.id, field.name, { source: 'value', value }), `value:${step.id}:${field.name}`)}
				/>
				<ClearButton field={field.name} onClear={() => set(null)} />
			</span>
		);
	}
	return (
		<span className='group/arg flex min-w-0 items-center gap-0.5'>
			<ArgPicker step={step} field={field}>
				<button
					type='button'
					aria-label={`${arg ? 'Change' : 'Set'} ${field.name}`}
					title={arg?.source === 'step' ? arg.path : undefined}
					className={cn(INTERACTIVE, CHIP, slotTone(arg, field.required), 'hover:brightness-95')}
				>
					<SlotContent arg={arg} steps={draft.steps} />
				</button>
			</ArgPicker>
			{arg && <ClearButton field={field.name} onClear={() => set(null)} />}
		</span>
	);
};

const ArgRow = ({ step, field }: { step: CustomToolStep; field: ToolField }) => (
	<Row>
		<Port id={argPort(field.name)} type='target' side='left' tone={argTone(step.args[field.name])} />
		<span title={field.description ?? undefined} className='min-w-0 flex-1 truncate font-mono text-[11px] leading-4 text-gray-900'>
			{field.name}
		</span>
		<ArgSlot step={step} field={field} />
	</Row>
);

interface StepArgsProps {
	step: CustomToolStep;
	fields: ToolField[];
	/** The brand's tools are still loading, so the tool's arguments are not known yet. */
	loading: boolean;
}

export const StepArgs = ({ step, fields, loading }: StepArgsProps) => {
	const { shown, unset, showOptional, toggleOptional } = useShownFields(step, fields);
	usePortsChanged(shown.map((field) => field.name).join(','));

	if (!fields.length) {
		return (
			<div className='border-t border-gray-300 py-1'>
				<Row>
					<span className='text-[11px] leading-4 text-gray-800'>{loading ? 'Loading' : 'Takes no arguments'}</span>
				</Row>
			</div>
		);
	}
	return (
		<div className='border-t border-gray-300 py-1'>
			{shown.map((field) => (
				<ArgRow key={field.name} step={step} field={field} />
			))}
			{unset > 0 && (
				<Row>
					<button
						type='button'
						aria-expanded={showOptional}
						onClick={toggleOptional}
						className={cn(INTERACTIVE, 'text-[11px] leading-4 text-gray-800 hover:text-ink')}
					>
						{showOptional ? 'Hide optional' : `+ ${unset} optional`}
					</button>
				</Row>
			)}
		</div>
	);
};
